// Using axios for OpenAI API calls instead of the openai package to avoid dependency issues
import axios from 'axios';

// Define types for better type safety
export interface KeywordData {
  occurrence: number;
  firstSeen: string;
  lastSeen: string;
  effectiveness?: number;
  category?: string;
}

export interface KeywordDatabase {
  [creatorName: string]: {
    [keyword: string]: KeywordData;
  };
}

/**
 * TypeScript implementation of the Python KeywordLearner class
 * This follows the same logic as the Python implementation
 */
export class KeywordLearner {
  private openaiApiKey: string;
  private keywordDb: KeywordDatabase = {};
  private readonly storageKey = 'keywordLearnerDb';

  constructor(openaiApiKey: string) {
    // Store OpenAI API key
    this.openaiApiKey = openaiApiKey;
    
    // Load keyword database from localStorage
    this.loadKeywordDatabase();
  }
  
  /**
   * Load keyword database from localStorage
   */
  private loadKeywordDatabase(): void {
    try {
      const storedData = localStorage.getItem(this.storageKey);
      if (storedData) {
        this.keywordDb = JSON.parse(storedData);
        console.log(`📊 Loaded keyword database with ${Object.keys(this.keywordDb).length} creators`);
      }
    } catch (e) {
      console.error('Failed to load keyword database:', e);
    }
  }
  
  /**
   * Save keyword database to localStorage
   */
  private saveKeywordDatabase(): void {
    try {
      localStorage.setItem(this.storageKey, JSON.stringify(this.keywordDb));
    } catch (e) {
      console.error('Failed to save keyword database:', e);
    }
  }

  /**
   * Learn keywords from scraped results
   */
  public async learnFromResults(results: any[], creatorName: string): Promise<string[]> {
    if (!results || results.length === 0) {
      console.log("❌ No results to learn from");
      return [];
    }

    console.log(`📊 Analyzing ${results.length} results`);

    // Extract text for analysis
    const textCorpus: string[] = [];
    for (const result of results) {
      if (result.title) textCorpus.push(result.title);
      if (result.snippet) textCorpus.push(result.snippet);
    }

    const fullText = textCorpus.filter(t => typeof t === 'string').join(" ");

    console.log(`📝 Extracted ${textCorpus.length} text elements for analysis`);

    // Extract potential keywords with basic NLP
    const extractedKeywords = this.extractKeywords(fullText);
    console.log(`🔍 Extracted ${extractedKeywords.length} candidate keywords`);

    // Generate optimized keywords with AI
    const aiKeywords = await this.generateAiKeywords(results, extractedKeywords, creatorName);
    console.log(`🧠 Generated ${aiKeywords.length} AI-optimized keywords`);

    // Update keywords database
    const updatedKeywords = this.updateKeywordDatabase(aiKeywords, creatorName);

    return updatedKeywords;
  }

  /**
   * Extract potential keywords from text
   */
  private extractKeywords(text: string): string[] {
    // Advanced word frequency analysis
    // Tokenize text into words and phrases
    const words = text.toLowerCase().match(/\b\w{4,}\b/g) || [];
    const phrases = this.extractPhrases(text);
    
    // Count word frequencies
    const wordCounts: Record<string, number> = {};
    for (const word of words) {
      if (word in wordCounts) {
        wordCounts[word]++;
      } else {
        wordCounts[word] = 1;
      }
    }
    
    // Add phrases to word counts with higher weight
    for (const phrase of phrases) {
      if (phrase in wordCounts) {
        wordCounts[phrase] += 2; // Give phrases higher weight
      } else {
        wordCounts[phrase] = 2;
      }
    }
    
    // Get words appearing multiple times, sorted by frequency
    const sortedWords = Object.entries(wordCounts)
      .filter(([word, count]) => {
        // Filter out common words that aren't useful for searches
        const commonWords = ['about', 'after', 'again', 'also', 'because', 'before', 'between', 'could', 'every', 'from', 'their', 'there', 'these', 'thing', 'think', 'those', 'what', 'when', 'where', 'which', 'with', 'would', 'your'];
        return count > 1 && !commonWords.includes(word);
      })
      .sort((a, b) => b[1] - a[1])
      .map(([word]) => word)
      .slice(0, 50);
    
    return sortedWords;
  }
  
  /**
   * Extract meaningful phrases from text
   */
  private extractPhrases(text: string): string[] {
    const phrases: string[] = [];
    
    // Look for 2-3 word phrases
    const words = text.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/);
    
    // Extract 2-word phrases
    for (let i = 0; i < words.length - 1; i++) {
      if (words[i].length >= 4 && words[i+1].length >= 4) {
        phrases.push(`${words[i]} ${words[i+1]}`);
      }
    }
    
    // Extract 3-word phrases
    for (let i = 0; i < words.length - 2; i++) {
      if (words[i].length >= 3 && words[i+1].length >= 3 && words[i+2].length >= 3) {
        phrases.push(`${words[i]} ${words[i+1]} ${words[i+2]}`);
      }
    }
    
    return phrases;
  }

  /**
   * Generate keywords using AI
   */
  private async generateAiKeywords(results: any[], extractedKeywords: string[], creatorName: string): Promise<string[]> {
    // Prepare data for OpenAI
    const resultsText = results.slice(0, 30).map(r => 
      `Title: ${r.title || 'N/A'}\nURL: ${r.url || 'N/A'}\nSnippet: ${r.snippet || 'N/A'}`
    ).join('\n\n').substring(0, 2000);

    // Load existing keywords from database
    const existingKeywords = this.getExistingKeywords(creatorName);
    const existingKeywordsText = existingKeywords.length > 0 
      ? existingKeywords.slice(0, 15).join(", ") 
      : "No previous keywords available";

    const prompt = `
    **Role: You are a Search Intelligence Analyst specializing in finding leaked content.**

    We need to generate optimal search keywords for finding leaked content of creator "${creatorName}".

    **PREVIOUSLY SUCCESSFUL KEYWORDS:**
    ${existingKeywordsText}

    **EXTRACTED FREQUENT WORDS:**
    ${extractedKeywords.slice(0, 30)}

    **SAMPLE CONTENT:**
    ${resultsText}

    **YOUR TASK:**
    - Analyze the content to identify keywords that would find more leaked content
    - Generate ONLY 8-10 search keywords with highest probability of finding leaked content
    - Each keyword should focus on finding unauthorized/leaked content
    - Focus on NSFW/adult terms that appear to be associated with leaked content
    - Include terms for file types, platforms, or specific content types
    - Do NOT include the creator's name in the keywords (it will be added separately)

    **FINAL OUTPUT:**
    Return only a properly formatted JavaScript array of 8-10 keywords, no explanations.
    Example: ["leaked onlyfans", "nude photos", "explicit content", "private video"]
    `;

    try {
      // Make OpenAI API call using axios - using a more modern model
      const response = await axios.post(
        'https://api.openai.com/v1/chat/completions',
        {
          model: "gpt-3.5-turbo",
          messages: [
            {
              role: "system",
              content: "You are a Search Intelligence Analyst specializing in finding leaked content."
            },
            {
              role: "user",
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 400
        },
        {
          headers: {
            'Authorization': `Bearer ${this.openaiApiKey}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const generatedText = response.data.choices[0].message.content.trim();
      console.log("📋 AI response received");

      // Process the response to extract keywords
      try {
        // Clean up the list format
        const cleanText = generatedText.replace(/\\n/g, ' ');

        // Find the list part
        const startIdx = cleanText.indexOf('[');
        const endIdx = cleanText.lastIndexOf(']');

        if (startIdx !== -1 && endIdx !== -1) {
          const listText = cleanText.substring(startIdx, endIdx + 1);
          // Convert string representation to actual list
          const keywords = JSON.parse(listText);
          return keywords;
        } else {
          // Fallback if list format not found
          return this.extractFallbackKeywords(generatedText);
        }
      } catch (e) {
        console.log(`❌ Error parsing AI keywords: ${e}`);
        console.log("🔄 Using fallback keyword extraction");
        return this.extractFallbackKeywords(generatedText);
      }
    } catch (e) {
      console.log(`❌ Error generating AI keywords: ${e}`);
      return [];
    }
  }

  /**
   * Extract keywords from text as fallback
   */
  private extractFallbackKeywords(text: string): string[] {
    // Simple extraction based on line breaks and quotes
    const keywords: string[] = [];
    const lines = text.split('\n');
    
    for (const line of lines) {
      const trimmedLine = line.trim();
      if (trimmedLine && trimmedLine.length > 4 && !trimmedLine.startsWith('#')) {
        // Extract anything in quotes
        if (trimmedLine.includes('"')) {
          const parts = trimmedLine.split('"');
          for (let i = 1; i < parts.length; i += 2) {
            if (parts[i].trim()) {
              keywords.push(parts[i].trim());
            }
          }
        }
        // Or just take the whole line if it's short
        else if (trimmedLine.length < 30 && !trimmedLine.startsWith('[') && !trimmedLine.endsWith(']')) {
          keywords.push(trimmedLine);
        }
      }
    }
    
    // Deduplicate and return max 10
    return Array.from(new Set(keywords)).slice(0, 10);
  }

  /**
   * Get existing keywords from database
   */
  private getExistingKeywords(creatorName: string): string[] {
    const normalizedName = creatorName.replace(/\\s+/g, '_');
    
    if (!this.keywordDb[normalizedName]) {
      return [];
    }
    
    // Sort by familiarity index (occurrence count)
    const sortedKeywords = Object.entries(this.keywordDb[normalizedName])
      .sort((a, b) => b[1].occurrence - a[1].occurrence)
      .map(([keyword]) => keyword);
      
    return sortedKeywords;
  }

  /**
   * Update keyword database with new keywords
   */
  private updateKeywordDatabase(aiKeywords: string[], creatorName: string): string[] {
    const normalizedName = creatorName.replace(/\s+/g, '_');
    
    // Create entry if it doesn't exist
    if (!this.keywordDb[normalizedName]) {
      this.keywordDb[normalizedName] = {};
    }
    
    // Update occurrence counts for keywords
    const currentDate = new Date().toISOString().split('T')[0];
    
    for (const keyword of aiKeywords) {
      const normalizedKeyword = keyword.toLowerCase().trim();
      
      if (this.keywordDb[normalizedName][normalizedKeyword]) {
        // Update existing keyword
        this.keywordDb[normalizedName][normalizedKeyword].occurrence += 1;
        this.keywordDb[normalizedName][normalizedKeyword].lastSeen = currentDate;
        
        // Update effectiveness score if it exists
        if (this.keywordDb[normalizedName][normalizedKeyword].effectiveness !== undefined) {
          // Slightly increase effectiveness each time it's found useful
          this.keywordDb[normalizedName][normalizedKeyword].effectiveness = 
            Math.min(1, (this.keywordDb[normalizedName][normalizedKeyword].effectiveness || 0) + 0.05);
        } else {
          // Initialize effectiveness score
          this.keywordDb[normalizedName][normalizedKeyword].effectiveness = 0.7;
        }
      } else {
        // Add new keyword
        this.keywordDb[normalizedName][normalizedKeyword] = {
          occurrence: 1,
          firstSeen: currentDate,
          lastSeen: currentDate,
          effectiveness: 0.5, // Initial effectiveness score
          category: this.categorizeKeyword(normalizedKeyword)
        };
      }
    }
    
    // Save to persistent storage
    this.saveKeywordDatabase();
    
    console.log(`✅ Updated keyword database with ${aiKeywords.length} keywords`);
    
    // Return all keywords sorted by occurrence
    return this.getExistingKeywords(creatorName);
  }
  
  /**
   * Categorize a keyword based on its content
   */
  private categorizeKeyword(keyword: string): string {
    const lowerKeyword = keyword.toLowerCase();
    
    // Define category patterns
    const categories: Record<string, string[]> = {
      'adult': ['nude', 'naked', 'xxx', 'porn', 'sex', 'adult', 'explicit', 'onlyfans'],
      'leak': ['leak', 'hack', 'stolen', 'private', 'exclusive'],
      'content': ['video', 'photo', 'image', 'picture', 'content', 'gallery'],
      'platform': ['instagram', 'twitter', 'facebook', 'tiktok', 'reddit', 'youtube']
    };
    
    // Check which category the keyword belongs to
    for (const [category, patterns] of Object.entries(categories)) {
      if (patterns.some(pattern => lowerKeyword.includes(pattern))) {
        return category;
      }
    }
    
    return 'general';
  }

  /**
   * Get suggested keywords for next search
   */
  public getSuggestedKeywords(creatorName: string, maxCount: number = 10): string[] {
    const keywords = this.getExistingKeywords(creatorName);
    
    // Return top keywords from database
    if (keywords.length > 0) {
      // Get a mix of categories for better coverage
      const categorizedKeywords = this.getCategorizedKeywords(creatorName);
      const result: string[] = [];
      
      // Add at least one keyword from each category if available
      const categories = ['adult', 'leak', 'content', 'platform', 'general'];
      for (const category of categories) {
        if (categorizedKeywords[category] && categorizedKeywords[category].length > 0) {
          result.push(categorizedKeywords[category][0]);
        }
      }
      
      // Fill the rest with top keywords
      const remainingCount = maxCount - result.length;
      if (remainingCount > 0) {
        // Filter out keywords already added
        const remainingKeywords = keywords.filter(kw => !result.includes(kw));
        result.push(...remainingKeywords.slice(0, remainingCount));
      }
      
      return result.slice(0, maxCount);
    } else {
      // Fallback to basic suggestions if no keywords in database
      return [
        "onlyfans leaks",
        "leaked content",
        "private photos",
        "nude leaks",
        "xxx content",
        "exclusive content",
        "leaked photos",
        "private videos",
        "premium content",
        "leaked account"
      ].slice(0, maxCount);
    }
  }
  
  /**
   * Get keywords categorized by type
   */
  private getCategorizedKeywords(creatorName: string): Record<string, string[]> {
    const normalizedName = creatorName.replace(/\s+/g, '_');
    const result: Record<string, string[]> = {
      'adult': [],
      'leak': [],
      'content': [],
      'platform': [],
      'general': []
    };
    
    if (!this.keywordDb[normalizedName]) {
      return result;
    }
    
    // Group keywords by category
    for (const [keyword, data] of Object.entries(this.keywordDb[normalizedName])) {
      const category = data.category || 'general';
      if (!result[category]) {
        result[category] = [];
      }
      result[category].push(keyword);
    }
    
    // Sort each category by occurrence
    for (const category of Object.keys(result)) {
      result[category].sort((a, b) => {
        const aData = this.keywordDb[normalizedName][a];
        const bData = this.keywordDb[normalizedName][b];
        return (bData.occurrence || 0) - (aData.occurrence || 0);
      });
    }
    
    return result;
  }
}
